<template>
  <div class="start">
    <header class="start-top">
      <router-link to="/" class="start-mark display-font"><img src="/favicon.svg" alt="" width="30" height="30">TrustClap</router-link>
      <div v-if="progress" class="start-progress" :aria-label="'Step ' + progress.at + ' of ' + progress.of">
        <span v-for="n in progress.of" :key="n" :class="['dot', { done: n < progress.at, now: n === progress.at }]"></span>
        <span class="start-progress-text">Step {{ progress.at }} of {{ progress.of }}</span>
      </div>
      <div class="start-account">
        <span v-if="email" class="start-email">{{ email }}</span>
        <router-link to="/logout" class="start-logout">Log out</router-link>
      </div>
    </header>

    <div v-if="loading" class="start-main"><div class="start-panel"><div class="pb-skeleton" style="height:44px;width:70%"></div><div class="pb-skeleton" style="height:180px;margin-top:24px"></div></div></div>

    <main v-else :class="['start-main', { 'with-preview': previewing }]">
      <div v-if="previewing" class="phone-switch pb-segmented" role="tablist" aria-label="Show">
        <button type="button" role="tab" :aria-selected="String(phoneView === 'edit')" @click="phoneView = 'edit'"><i class="mdi mdi-pencil-outline pb-ico" aria-hidden="true"></i> Details</button>
        <button type="button" role="tab" :aria-selected="String(phoneView === 'preview')" @click="phoneView = 'preview'"><i class="mdi mdi-cellphone pb-ico" aria-hidden="true"></i> Preview</button>
      </div>
      <section class="start-panel" :class="{ 'phone-hidden': previewing && phoneView === 'preview' }" aria-live="polite">
        <div v-if="error" class="pb-alert is-error" role="alert"><i class="mdi mdi-alert-circle-outline pb-ico" aria-hidden="true"></i><span>{{ error }}</span></div>

        <!-- 1. Where are you starting from? -->
        <template v-if="step === 'path'">
          <p class="pb-kicker">Get started · 5 to 10 minutes</p>
          <h1 class="start-title">Let's get your first testimonial live.</h1>
          <p class="start-lead">Pick where you're starting from. You can do everything else later.</p>
          <div class="path-cards">
            <button v-for="card in pathCards" :key="card.key" type="button" :class="['path-card', { picked: path === card.key }]" @click="choosePath(card.key)">
              <span class="path-icon" aria-hidden="true"><i :class="['mdi', card.icon]"></i></span>
              <span class="path-copy"><strong class="display-font">{{ card.title }}</strong><span>{{ card.text }}</span></span>
              <span class="path-time">{{ card.time }}</span>
              <i class="mdi mdi-arrow-right path-arrow" aria-hidden="true"></i>
            </button>
          </div>
          <p class="start-foot"><i class="mdi mdi-shield-check-outline" aria-hidden="true"></i> Nothing goes public until you publish it.</p>
        </template>

        <!-- Agency identity -->
        <template v-else-if="step === 'agency'">
          <p class="pb-kicker">Agency workspace</p>
          <h1 class="start-title">Set up your agency workspace.</h1>
          <p class="start-lead">Client approval pages and client reports show your agency's name and logo, not ours.</p>
          <form class="start-form" @submit.prevent="saveAgency">
            <div class="pb-field">
              <label for="agency-name" class="pb-label">Agency name</label>
              <input id="agency-name" v-model.trim="agencyName" class="pb-input" maxlength="120" required placeholder="e.g. Fernhollow Studio" autocomplete="organization">
            </div>
            <div class="pb-field">
              <span id="agency-logo-label" class="pb-label">Agency logo <span class="optional">(optional)</span></span>
              <LogoPicker v-model="agencyLogo" labelled-by="agency-logo-label" />
            </div>
            <div class="toolkit">
              <h2 class="toolkit-title"><i class="mdi mdi-briefcase-outline" aria-hidden="true"></i> Your agency toolkit</h2>
              <ul>
                <li v-for="line in toolkitLines" :key="line">{{ line }}</li>
              </ul>
              <p v-if="toolkitPlan && plan !== toolkitPlan">These come with {{ planName(toolkitPlan) }} at {{ toolkitPrice }} a month. On {{ planName(plan) }} you can manage {{ limits.brands === 1 ? 'one client brand' : limits.brands + ' client brands' }} today. <router-link :to="{ path: '/subscribe', query: { plan: toolkitPlan } }">See {{ planName(toolkitPlan) }}</router-link></p>
              <p v-else-if="toolkitPlan">Your workspace is on {{ planName(plan) }}: all of these are ready.</p>
            </div>
            <div class="start-actions">
              <button type="button" class="pb-button ghost" @click="go('path')">Back</button>
              <button type="submit" class="pb-button lg" :disabled="busy || !agencyName">{{ busy ? 'Saving…' : 'Continue' }}<i class="mdi mdi-arrow-right pb-ico" aria-hidden="true"></i></button>
            </div>
          </form>
        </template>

        <!-- Brand basics -->
        <template v-else-if="step === 'brand'">
          <p class="pb-kicker">{{ path === 'agency' ? 'First client' : 'Your business' }}</p>
          <h1 class="start-title">{{ path === 'agency' ? 'Add your first client.' : 'Tell us about the business.' }}</h1>
          <p class="start-lead">Customers see this name, logo and colour on the feedback page. You can change them any time.</p>
          <form class="start-form" @submit.prevent="saveBrand">
            <div class="pb-field">
              <label for="brand-name" class="pb-label">{{ path === 'agency' ? 'Client\'s business name' : 'Business name' }}</label>
              <input id="brand-name" v-model.trim="brand.name" class="pb-input" maxlength="120" required placeholder="e.g. Brindle & Rye Bakery" autocomplete="organization">
            </div>
            <div class="pb-field">
              <label for="brand-site" class="pb-label">Website <span class="optional">(optional)</span></label>
              <input id="brand-site" v-model.trim="brand.website_url" class="pb-input" type="url" inputmode="url" maxlength="500" placeholder="https://" autocomplete="url">
            </div>
            <div class="pb-field">
              <span id="brand-colour-label" class="pb-label">Brand colour</span>
              <div class="swatches" role="radiogroup" aria-labelledby="brand-colour-label">
                <button v-for="swatch in swatches" :key="swatch.hex" type="button" role="radio" :aria-checked="String(brand.color === swatch.hex)"
                        :aria-label="swatch.name" :class="['swatch', { on: brand.color === swatch.hex }]" :style="{ background: swatch.hex }"
                        @click="brand.color = swatch.hex"><i v-if="brand.color === swatch.hex" class="mdi mdi-check" aria-hidden="true"></i></button>
                <label class="swatch custom" :class="{ on: customColour }" :style="customColour ? { background: brand.color } : null">
                  <input type="color" :value="brand.color" aria-label="Choose any colour" @input="brand.color = normal($event.target.value)">
                  <i class="mdi mdi-eyedropper-variant" aria-hidden="true"></i>
                </label>
                <input v-model="hexText" class="pb-input hex pb-mono" maxlength="7" aria-label="Brand colour as a hex code" spellcheck="false" @change="applyHex">
              </div>
              <span v-if="lightColour" class="pb-help">This colour is light, so buttons on your page use dark text to stay readable.</span>
            </div>
            <div class="pb-field">
              <span id="brand-logo-label" class="pb-label">Logo <span class="optional">(optional)</span></span>
              <LogoPicker v-model="brandLogo" labelled-by="brand-logo-label" />
            </div>
            <div class="start-actions">
              <button type="button" class="pb-button ghost" @click="go(path === 'agency' ? 'agency' : 'path')">Back</button>
              <button type="submit" class="pb-button lg" :disabled="busy || !brand.name">{{ busy ? 'Saving…' : 'Continue' }}<i class="mdi mdi-arrow-right pb-ico" aria-hidden="true"></i></button>
            </div>
          </form>
        </template>

        <!-- Agency: collect or import for this client -->
        <template v-else-if="step === 'choose'">
          <p class="pb-kicker">{{ createdBrand ? createdBrand.name : 'Your client' }}</p>
          <h1 class="start-title">How do you want to start?</h1>
          <p class="start-lead">Collect fresh feedback from {{ createdBrand ? createdBrand.name + '\'s' : 'your client\'s' }} customers, or bring in testimonials they already have.</p>
          <div class="path-cards">
            <button v-for="card in pathCards.slice(0, 2)" :key="card.key" type="button" class="path-card" @click="go(card.key === 'collect' ? 'template' : 'paste')">
              <span class="path-icon" aria-hidden="true"><i :class="['mdi', card.icon]"></i></span>
              <span class="path-copy"><strong class="display-font">{{ card.title }}</strong><span>{{ card.text }}</span></span>
              <span class="path-time">{{ card.time }}</span>
              <i class="mdi mdi-arrow-right path-arrow" aria-hidden="true"></i>
            </button>
          </div>
        </template>

        <!-- Collect: template -->
        <template v-else-if="step === 'template'">
          <p class="pb-kicker">Collection page</p>
          <h1 class="start-title">Pick a starting point.</h1>
          <p class="start-lead">Guided questions help customers write something specific. You can edit the wording next.</p>
          <div class="templates" role="radiogroup" aria-label="Question templates">
            <button v-for="item in templates" :key="item.key" type="button" role="radio" :aria-checked="String(templateKey === item.key)"
                    :aria-disabled="String(item.locked)" :class="['template', { on: templateKey === item.key, locked: item.locked }]"
                    @click="pickTemplate(item)">
              <span class="template-top"><strong>{{ item.name }}</strong><PlanChip v-if="item.locked" :plan="templatePlan" /></span>
              <span class="template-text">{{ item.description }}</span>
              <span class="template-q display-font">“{{ item.questions[1] ? item.questions[1].label : item.questions[0].label }}”</span>
            </button>
          </div>
          <p v-if="templates.some(item => item.locked)" class="pb-help">{{ planName(plan) }} includes the General and Service templates. {{ templatePlans }} add the full library and your own questions.</p>
          <!-- As wide as the template grid, so "Use this template" lines up with the cards' right edge. -->
          <div class="start-actions wide">
            <button type="button" class="pb-button ghost" @click="go(path === 'agency' ? 'choose' : 'brand')">Back</button>
            <button type="button" class="pb-button lg" :disabled="busy || !templateKey" @click="createForm">{{ busy ? 'Creating…' : 'Use this template' }}<i class="mdi mdi-arrow-right pb-ico" aria-hidden="true"></i></button>
          </div>
        </template>

        <!-- Collect: headline and questions -->
        <template v-else-if="step === 'questions'">
          <p class="pb-kicker">Collection page</p>
          <h1 class="start-title">Make it yours.</h1>
          <p class="start-lead">Short and specific works best: about two minutes to answer.</p>
          <form class="start-form" @submit.prevent="saveQuestions">
            <div class="pb-field">
              <label for="form-headline" class="pb-label">Headline</label>
              <input id="form-headline" v-model="content.headline" class="pb-input" maxlength="120" required>
            </div>
            <div class="pb-field">
              <label for="form-intro" class="pb-label">Introduction <span class="optional">(optional)</span></label>
              <textarea id="form-intro" v-model="content.intro" class="pb-textarea" rows="3" maxlength="600"></textarea>
            </div>
            <fieldset v-if="!customQuestions" class="pb-field questions-pick">
              <legend class="pb-label">Questions <span class="optional">(up to 3 on Free)</span></legend>
              <label v-for="question in templateQuestions" :key="question.id" class="pb-check">
                <input type="checkbox" :checked="hasQuestion(question)" :disabled="!hasQuestion(question) && content.questions.length >= 3" @change="toggleQuestion(question)">
                <span>{{ question.label }}<span v-if="question.quotable" class="pb-chip is-accent quotable">Quoted on your site</span></span>
              </label>
            </fieldset>
            <div v-else class="pb-field">
              <span class="pb-label">Questions</span>
              <div v-for="(question, index) in content.questions" :key="question.id" class="question-edit">
                <input v-model="question.label" class="pb-input" maxlength="200" :aria-label="'Question ' + (index + 1)">
                <button type="button" class="pb-icon-button" :aria-label="'Remove question ' + (index + 1)" :disabled="content.questions.length < 2" @click="content.questions.splice(index, 1)"><i class="mdi mdi-close"></i></button>
              </div>
              <button v-if="content.questions.length < 10" type="button" class="pb-button ghost sm" @click="addQuestion"><i class="mdi mdi-plus pb-ico" aria-hidden="true"></i>Add a question</button>
            </div>
            <div class="start-actions">
              <button type="button" class="pb-button ghost" @click="go('template')">Back</button>
              <button type="submit" class="pb-button lg" :disabled="busy || !content.questions.length">{{ busy ? 'Saving…' : 'Publish my page' }}<i class="mdi mdi-arrow-right pb-ico" aria-hidden="true"></i></button>
            </div>
          </form>
        </template>

        <!-- Collect: done -->
        <template v-else-if="step === 'live'">
          <div class="done-mark" aria-hidden="true"><i class="mdi mdi-check"></i></div>
          <h1 class="start-title">Your page is live.</h1>
          <p class="start-lead">Share the link or the QR code. New feedback lands in your inbox, and nothing is published until you say so.</p>
          <!-- "Go to my dashboard" is this screen's one solid button; the Copy buttons are secondary. -->
          <SnippetBox :value="formLink" label="Your collection link" secondary />
          <div class="share-grid">
            <div class="share-qr">
              <span class="pb-label">QR code for the counter or an invoice</span>
              <QrCode :value="formLink" :size="148" :filename="'trustclap-' + (createdBrand ? createdBrand.slug : 'qr')" />
            </div>
            <div class="share-message">
              <SnippetBox :value="emailMessage" label="Email request, ready to send" multiline prose :rows="6" secondary />
            </div>
          </div>
          <div class="start-actions live">
            <a :href="formLink" target="_blank" rel="noopener" class="pb-button outline"><i class="mdi mdi-open-in-new pb-ico" aria-hidden="true"></i>Open the page</a>
            <button type="button" class="pb-button lg" @click="finish">Go to my dashboard<i class="mdi mdi-arrow-right pb-ico" aria-hidden="true"></i></button>
          </div>
        </template>

        <!-- Import: paste -->
        <template v-else-if="step === 'paste'">
          <p class="pb-kicker">Import</p>
          <h1 class="start-title">Add the testimonials you already have.</h1>
          <p class="start-lead">From emails, messages or an old website. Paste them below: one testimonial per paragraph.</p>
          <form class="start-form" @submit.prevent="importPasted">
            <div class="pb-field">
              <label for="paste-text" class="pb-label">Testimonials</label>
              <textarea id="paste-text" v-model="pasteText" class="pb-textarea" rows="7" placeholder="Paste one testimonial per paragraph.

Leave an empty line between them. A last line such as “- Jane Doe, Acme” becomes the name and company."></textarea>
              <span class="pb-help">{{ blocks.length ? blocks.length + ' testimonial' + (blocks.length === 1 ? '' : 's') + ' found.' : 'You can add more any time from Testimonials.' }}</span>
            </div>
            <div v-for="(block, index) in blocks" :key="index" class="block-row">
              <p class="block-text display-font">{{ block.text }}</p>
              <div class="block-fields">
                <input v-model="block.name" class="pb-input" maxlength="120" :aria-label="'Name for testimonial ' + (index + 1)" placeholder="Name">
                <input v-model="block.company" class="pb-input" maxlength="120" :aria-label="'Company for testimonial ' + (index + 1)" placeholder="Company (optional)">
              </div>
            </div>
            <div class="pb-field">
              <label for="paste-source" class="pb-label">Where is this from?</label>
              <input id="paste-source" v-model.trim="pasteSource" class="pb-input" maxlength="160" required placeholder="e.g. Email from client, March 2026">
            </div>
            <label class="pb-check attest"><input v-model="attest" type="checkbox" required> <span>I confirm I have permission to publish these testimonials and the details shown with them.</span></label>
            <div class="start-actions">
              <button type="button" class="pb-button ghost" @click="go(path === 'agency' ? 'choose' : 'brand')">Back</button>
              <button type="submit" class="pb-button lg" :disabled="busy || !blocks.length || !pasteSource || !attest">{{ busy ? 'Importing…' : 'Import ' + blocks.length + ' testimonial' + (blocks.length === 1 ? '' : 's') }}</button>
            </div>
          </form>
        </template>

        <!-- Import: publish -->
        <template v-else-if="step === 'publish'">
          <p class="pb-kicker">Import</p>
          <h1 class="start-title">{{ imported.length }} testimonial{{ imported.length === 1 ? '' : 's' }} imported as approved.</h1>
          <p class="start-lead">They show the source you gave. Publish them to put them on your widget and hosted wall.</p>
          <div class="imported">
            <article v-for="item in imported" :key="item.id" class="pb-quote small">
              <p class="pb-quote-text">{{ item.text }}</p>
              <p class="pb-quote-meta">{{ item.name || 'A customer of ' + (createdBrand ? createdBrand.name : 'your business') }}<template v-if="item.company"> · {{ item.company }}</template></p>
              <p v-if="item.source_label" class="pb-quote-meta">{{ item.source_label }}</p>
            </article>
          </div>
          <div class="start-actions">
            <button type="button" class="pb-button ghost" @click="go('widget')">Choose later</button>
            <button type="button" class="pb-button lg" :disabled="busy || !imported.length" @click="publishImported">{{ busy ? 'Publishing…' : 'Publish these ' + imported.length + ' testimonial' + (imported.length === 1 ? '' : 's') }}</button>
          </div>
        </template>

        <!-- Import: widget -->
        <template v-else-if="step === 'widget'">
          <p class="pb-kicker">Publish</p>
          <h1 class="start-title">{{ widget ? 'Paste this once. It keeps itself current.' : 'Show them on your website.' }}</h1>
          <p class="start-lead">{{ widget ? 'Add the snippet to your page. New testimonials you publish appear on their own.' : 'Pick a layout. You can change it any time.' }}</p>
          <template v-if="!widget">
            <div class="layouts" role="radiogroup" aria-label="Widget layout">
              <button v-for="item in layouts" :key="item.key" type="button" role="radio" :aria-checked="String(layout === item.key)"
                      :aria-disabled="String(item.locked)" :class="['layout', { on: layout === item.key, locked: item.locked }]" @click="pickLayout(item)">
                <span :class="['layout-thumb', item.key]" aria-hidden="true"><i></i><i></i><i></i></span>
                <strong>{{ item.name }}</strong>
                <PlanChip v-if="item.locked" :plan="layoutPlan" />
              </button>
            </div>
            <div class="start-actions">
              <button type="button" class="pb-button ghost" @click="finish">Skip for now</button>
              <button type="button" class="pb-button lg" :disabled="busy" @click="createWidget">{{ busy ? 'Creating…' : 'Create my widget' }}</button>
            </div>
          </template>
          <template v-else>
            <SnippetBox :value="snippet" label="Your widget code" multiline :rows="4" />
            <p class="pb-help">Works on plain HTML, WordPress, Webflow, Squarespace, Wix, Framer and Shopify. Help has step-by-step notes.</p>
            <div class="start-actions">
              <button type="button" class="pb-button outline" :disabled="busy" @click="openWall"><i class="mdi mdi-open-in-new pb-ico" aria-hidden="true"></i>Open your hosted wall</button>
              <button type="button" class="pb-button lg" @click="finish">Go to my dashboard<i class="mdi mdi-arrow-right pb-ico" aria-hidden="true"></i></button>
            </div>
          </template>
        </template>
      </section>

      <aside v-if="previewing" class="start-preview" :class="{ 'phone-hidden': phoneView !== 'preview' }" aria-label="Preview of your collection page">
        <p class="preview-label"><i class="mdi mdi-eye-outline" aria-hidden="true"></i> What your customers see</p>
        <PhoneFrame label="Collection page preview">
          <CollectFormPreview :form="previewForm" :brand="previewBrand" mode="preview" compact :attribution="plan === 'free'" />
        </PhoneFrame>
        <!-- On phones the preview is its own tab, so the way forward is repeated under it. -->
        <div class="preview-actions">
          <button type="button" class="pb-button ghost" @click="phoneView = 'edit'"><i class="mdi mdi-pencil-outline pb-ico" aria-hidden="true"></i>Edit details</button>
          <button type="button" class="pb-button lg" :disabled="busy" @click="runPrimary">{{ primaryLabel }}<i class="mdi mdi-arrow-right pb-ico" aria-hidden="true"></i></button>
        </div>
      </aside>
    </main>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import CollectFormPreview, { defaultFields } from '../components/CollectFormPreview.vue';
import PhoneFrame from '../components/PhoneFrame.vue';
import LogoPicker from '../components/LogoPicker.vue';
import SnippetBox from '../components/SnippetBox.vue';
import PlanChip from '../components/PlanChip.vue';
import QrCode from '../components/QrCode.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import { get, post, patch, uploadForm, message, planLimit } from '../api';
import { SWATCHES, DEFAULT_COLOR, normalHex, contrast } from '../lib/color';
import { collectUrl, wallUrl, embedSnippet, imageUrl } from '../publicUrls';
import { parseBlocks } from '../lib/paste';
import { addedLines, allowsMore, monthlyPrice, planLabel, planOrder } from '../lib/plans';

const PATHS = [
  { key: 'collect', icon: 'mdi-link-variant', title: 'Collect new testimonials', text: 'Create a collection page and get a link to share.', time: 'About 5 minutes' },
  { key: 'import', icon: 'mdi-import', title: 'I already have testimonials', text: 'Import them and publish a widget today.', time: 'About 10 minutes' },
  { key: 'agency', icon: 'mdi-domain', title: 'I manage client websites', text: 'Set up your agency workspace and first client.', time: 'About 10 minutes' }
];
const FLOWS = {
  collect: ['brand', 'template', 'questions', 'live'],
  import: ['brand', 'paste', 'publish', 'widget'],
  agency: ['agency', 'brand', 'choose', 'template', 'questions', 'live'],
  agencyImport: ['agency', 'brand', 'choose', 'paste', 'publish', 'widget']
};
const LAYOUTS = [{ key: 'grid', name: 'Grid' }, { key: 'cards', name: 'Cards' }, { key: 'carousel', name: 'Carousel' }, { key: 'quote', name: 'Single quote' }];

export default {
  name: 'StartView',
  components: { CollectFormPreview, PhoneFrame, LogoPicker, PlanChip, SnippetBox, QrCode, UpgradeDialog },
  data() {
    const query = this.$route.query || {};
    const name = typeof query.name === 'string' ? query.name.slice(0, 120) : '';
    const color = normalHex(typeof query.color === 'string' ? query.color : '', DEFAULT_COLOR);
    const requested = ['collect', 'import', 'agency'].indexOf(query.path) >= 0 ? query.path : (name ? 'collect' : '');
    return {
      loading: true, busy: false, error: '', upgrade: null, upgradeOpen: false,
      step: requested ? (requested === 'agency' ? 'agency' : 'brand') : 'path', path: requested, phoneView: 'edit',
      agencyName: '', agencyLogo: null,
      brand: { name, website_url: '', color }, hexText: color, brandLogo: null, createdBrand: null,
      templates: [], messages: [], templateKey: 'general', form: null,
      content: { headline: 'How was your experience with us?', intro: 'Your answers help us improve, and you decide whether we may share them.', questions: [], fields: defaultFields(), allow_anonymous: false, thank_you: '' },
      pasteText: '', blocks: [], pasteSource: '', attest: false, imported: [],
      layout: 'grid', widget: null
    };
  },
  computed: {
    email() { const user = this.$store.getters.currentUser; return user ? user.email : ''; },
    plan() { return this.$store.getters['praiseboard/plan']; },
    limits() { return this.$store.getters['praiseboard/limits']; },
    customQuestions() { return !!this.limits.custom_questions; },
    planMatrix() { return this.$store.getters['praiseboard/planMatrix']; },
    // The agency toolkit is what the plan with client brands adds over the plan below it (the plan matrix).
    toolkitPlan() { return this.$store.getters['praiseboard/availableOn']('brands') || (this.planMatrix[this.plan] ? this.plan : ''); },
    toolkitLines() {
      const order = planOrder(this.planMatrix);
      const below = order[order.indexOf(this.toolkitPlan) - 1];
      return below ? addedLines(this.planMatrix, below, this.toolkitPlan) : [];
    },
    toolkitPrice() { return monthlyPrice(this.planMatrix, this.toolkitPlan); },
    templatePlan() { return this.$store.getters['praiseboard/availableOn']('template_library') || ''; },
    layoutPlan() { return this.$store.getters['praiseboard/availableOn']('layouts') || ''; },
    templatePlans() {
      const names = planOrder(this.planMatrix).filter(name => allowsMore(this.planMatrix[name].limits.template_library, this.limits.template_library))
        .map(name => this.planName(name));
      return names.length > 1 ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1] : names.join('');
    },
    swatches() { return SWATCHES; },
    customColour() { return !SWATCHES.some(swatch => swatch.hex === this.brand.color); },
    lightColour() { return contrast(this.brand.color, '#FFFFFF') < 4.5; },
    pathCards() { return PATHS; },
    flow() {
      if (this.path === 'agency') return this.step === 'paste' || this.step === 'publish' || this.step === 'widget' ? FLOWS.agencyImport : FLOWS.agency;
      return FLOWS[this.path] || null;
    },
    progress() {
      if (!this.flow || this.step === 'path') return null;
      const index = this.flow.indexOf(this.step);
      return index >= 0 ? { at: index + 1, of: this.flow.length } : null;
    },
    previewing() { return ['brand', 'questions', 'template'].indexOf(this.step) >= 0; },
    templateQuestions() {
      const item = this.templates.find(t => t.key === this.templateKey);
      return item ? item.questions : [];
    },
    previewForm() {
      if (this.step === 'brand' || !this.content.questions.length) {
        const item = this.templates.find(t => t.key === this.templateKey);
        const questions = item ? item.questions.slice(0, 2) : [
          { id: 'p1', label: 'What did you buy or use from us?', help: 'A few words is plenty.' },
          { id: 'p2', label: 'How has it helped you?', help: 'What is better, easier or different now?' }];
        // Before a template is chosen the preview shows the essentials: two questions, the stars,
        // a name, the separate permission choice and the send button.
        const fields = { rating: 'optional', name: 'off', title: 'off', company: 'off', photo: 'off', contact: 'off' };
        // A template with its own headline (the service one) shows it, so the headline never repeats a question.
        const headline = (item && item.headline) || this.content.headline;
        return Object.assign({}, this.content, { headline, questions, fields });
      }
      return this.content;
    },
    previewBrand() {
      const logo = this.brandLogo && this.brandLogo.preview;
      // No name yet: the preview labels its header "Your business" and its sentences say "we".
      return { name: this.brand.name || '', color: this.brand.color, logo_url: logo || '' };
    },
    formLink() { return collectUrl(this.form); },
    emailMessage() {
      const text = (this.messages[0] && this.messages[0].text) || 'Hi {customer}, would you share a few words about your experience with {business}? {link}';
      return text.replace('{customer}', 'there').replace('{business}', this.createdBrand ? this.createdBrand.name : this.brand.name).replace('{link}', this.formLink);
    },
    layouts() {
      const allowed = this.limits.layouts || ['grid'];
      return LAYOUTS.map(item => Object.assign({}, item, { locked: allowed.indexOf(item.key) < 0 }));
    },
    snippet() { return embedSnippet(this.widget, this.createdBrand); },
    primaryLabel() {
      if (this.busy) return 'Saving…';
      return { brand: 'Continue', template: 'Use this template', questions: 'Publish my page' }[this.step] || 'Continue';
    }
  },
  watch: {
    'brand.color'(value) { this.hexText = value; },
    pasteText(value) { this.blocks = parseBlocks(value); }
  },
  async mounted() {
    try {
      const data = await this.$store.dispatch('praiseboard/load');
      if (data && data.workspace && (data.brands || []).length && !this.$route.query.again) {
        this.$router.replace('/').catch(() => {});
        return;
      }
      if (data && data.workspace) {
        this.agencyName = data.workspace.name;
        if (data.workspace.kind === 'agency' && !this.path) this.path = 'agency';
        if (this.step === 'agency' || this.step === 'path') this.step = this.path ? 'brand' : 'path';
      }
      const library = await get('/question-templates');
      this.templates = library.templates || [];
      this.messages = library.messages || [];
    } catch (error) {
      // The first step works without the server; later steps report their own errors.
      if (error && error.response && error.response.status !== 404) this.error = message(error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    planName(name) { return planLabel(this.planMatrix, name); },
    normal(value) { return normalHex(value); },
    // The preview tab's button does what the step's own primary button does.
    runPrimary() {
      if (this.step === 'brand') {
        if (!this.brand.name) {
          this.phoneView = 'edit';
          this.$nextTick(() => { const field = document.getElementById('brand-name'); if (field) field.focus(); });
          return;
        }
        this.saveBrand();
      } else if (this.step === 'template') {
        this.createForm();
      } else if (this.step === 'questions') {
        if (!this.content.questions.length) { this.phoneView = 'edit'; return; }
        this.saveQuestions();
      }
    },
    applyHex() { this.brand.color = normalHex(this.hexText, this.brand.color); this.hexText = this.brand.color; },
    go(step) { this.error = ''; this.step = step; this.phoneView = 'edit'; window.scrollTo(0, 0); },
    choosePath(key) { this.path = key; this.go(key === 'agency' ? (this.$store.getters['praiseboard/workspace'] ? 'brand' : 'agency') : 'brand'); },
    fail(error) {
      const limit = planLimit(error);
      if (limit) { this.upgrade = limit; this.upgradeOpen = true; return; }
      this.error = message(error);
    },
    async ensureWorkspace(name, kind) {
      if (this.$store.getters['praiseboard/workspace']) return;
      await post('/workspace', { name, kind });
      await this.$store.dispatch('praiseboard/load');
    },
    async uploadLogo(path, picked) {
      if (!picked || !picked.file) return;
      const body = new FormData();
      body.append('file', picked.file);
      try { await uploadForm(path, body); } catch (error) { this.error = 'Your logo could not be saved: ' + message(error) + ' You can add it later in Settings.'; }
    },
    async saveAgency() {
      this.busy = true; this.error = '';
      try {
        await this.ensureWorkspace(this.agencyName, 'agency');
        await this.uploadLogo('/workspace/logo', this.agencyLogo);
        this.go('brand');
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    async saveBrand() {
      this.busy = true; this.error = '';
      try {
        await this.ensureWorkspace(this.path === 'agency' ? (this.agencyName || this.brand.name) : this.brand.name, this.path === 'agency' ? 'agency' : 'business');
        if (!this.createdBrand) {
          this.createdBrand = await post('/brands', { name: this.brand.name, website_url: this.brand.website_url || null, color: this.brand.color });
        } else {
          this.createdBrand = await patch('/brands/' + this.createdBrand.id, { name: this.brand.name, website_url: this.brand.website_url || null, color: this.brand.color });
        }
        await this.uploadLogo('/brands/' + this.createdBrand.id + '/logo', this.brandLogo);
        this.$store.dispatch('praiseboard/selectBrand', this.createdBrand.id);
        await this.$store.dispatch('praiseboard/load');
        this.go(this.path === 'agency' ? 'choose' : (this.path === 'import' ? 'paste' : 'template'));
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    pickTemplate(item) {
      if (item.locked) { this.upgrade = { feature: 'template_library' }; this.upgradeOpen = true; return; }
      this.templateKey = item.key;
    },
    async createForm() {
      this.busy = true; this.error = '';
      try {
        this.form = await post('/brands/' + this.createdBrand.id + '/forms', { template_key: this.templateKey });
        this.content = JSON.parse(JSON.stringify(this.form.content));
        this.go('questions');
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    hasQuestion(question) { return this.content.questions.some(q => q.label === question.label); },
    toggleQuestion(question) {
      if (this.hasQuestion(question)) this.content.questions = this.content.questions.filter(q => q.label !== question.label);
      else if (this.content.questions.length < 3) {
        const order = this.templateQuestions.map(q => q.label);
        this.content.questions = this.content.questions.concat([Object.assign({ required: false }, question)])
          .sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));
      }
    },
    addQuestion() {
      this.content.questions.push({ id: 'q' + Date.now().toString(36), label: '', help: '', required: false, quotable: false });
    },
    async saveQuestions() {
      this.busy = true; this.error = '';
      try {
        this.content.questions = this.content.questions.filter(q => (q.label || '').trim());
        this.form = await patch('/forms/' + this.form.id, { content: this.content });
        this.go('live');
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    async importPasted() {
      this.busy = true; this.error = '';
      try {
        const rows = this.blocks.map(block => ({ text: block.text, name: block.name || null, company: block.company || null }));
        const result = await post('/brands/' + this.createdBrand.id + '/imports', { rows, source_label: this.pasteSource, attest: this.attest, origin: 'paste' });
        this.imported = (result.submissions || []).map(item => ({ id: item.id, text: item.text, name: item.name, company: item.company, source_label: item.source_label }));
        this.go('publish');
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    async publishImported() {
      this.busy = true; this.error = '';
      try {
        const result = await post('/submissions/actions', { ids: this.imported.map(item => item.id), action: 'publish' });
        await this.$store.dispatch('praiseboard/load');
        if (result.limit) { this.upgrade = result.limit; this.upgradeOpen = true; }
        this.go('widget');
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    pickLayout(item) {
      if (item.locked) { this.upgrade = { feature: 'layouts' }; this.upgradeOpen = true; return; }
      this.layout = item.key;
    },
    async createWidget() {
      this.busy = true; this.error = '';
      try {
        this.widget = await post('/brands/' + this.createdBrand.id + '/widgets', { name: 'Website widget', layout: this.layout });
      } catch (error) { this.fail(error); } finally { this.busy = false; }
    },
    async openWall() {
      this.busy = true; this.error = '';
      const tab = window.open('', '_blank');
      try {
        await patch('/brands/' + this.createdBrand.id + '/wall', { wall_status: 'live' });
        const url = wallUrl(this.createdBrand);
        if (tab) tab.location = url; else window.location.href = url;
      } catch (error) { if (tab) tab.close(); this.fail(error); } finally { this.busy = false; }
    },
    async finish() {
      try { await patch('/workspace', { onboarding: { path: this.path, finished: true } }); } catch (error) { /* the dashboard still works */ }
      await this.$store.dispatch('praiseboard/load').catch(() => {});
      this.$router.push('/').catch(() => {});
    },
    imageUrl
  }
};
</script>

<style scoped>
.start{min-height:100vh;background:#FAF7F2;background-image:radial-gradient(rgba(31,35,40,.06) 1px,transparent 1.4px);background-size:22px 22px}
.start-top{position:sticky;top:0;z-index:5;display:flex;align-items:center;gap:20px;padding:14px max(20px,calc((100vw - 1200px)/2));
  background:rgba(250,247,242,.88);-webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);border-bottom:1px solid #E6DED2}
.start-mark{display:inline-flex;align-items:center;gap:10px;color:#1F2328;text-decoration:none;font:600 19px/1 var(--font-display);letter-spacing:-.3px}
.start-mark img{border-radius:8px}
.start-progress{display:flex;align-items:center;gap:6px;margin-left:auto}
.start-progress .dot{width:22px;height:4px;border-radius:4px;background:#E6DED2}
.start-progress .dot.done{background:rgba(180,71,43,.45)}
.start-progress .dot.now{background:#B4472B}
.start-progress-text{margin-left:8px;font-size:13px;color:#6B635B}
.start-account{display:flex;align-items:center;gap:14px;margin-left:auto;font-size:13.5px}
.start-progress+.start-account{margin-left:28px}
.start-email{color:#6B635B;max-width:220px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.start-logout{color:#963A22;font-weight:600;min-height:44px;display:inline-flex;align-items:center;padding:0 8px;white-space:nowrap}
.start-main{max-width:1200px;margin:0 auto;padding:56px 20px 96px;display:grid;grid-template-columns:minmax(0,760px);justify-content:center}
.start-main.with-preview{grid-template-columns:minmax(0,1fr) 360px;gap:64px;justify-content:stretch}
.start-panel{min-width:0}
.start-title{margin:0 0 12px;font:500 44px/1.1 var(--font-display);letter-spacing:-1px;color:#1F2328;text-wrap:balance}
.start-lead{margin:0 0 30px;max-width:620px;font-size:17px;line-height:1.6;color:#4F4A45}
.start-form{max-width:560px}
.start-foot{display:flex;align-items:center;gap:8px;margin-top:22px;color:#6B635B;font-size:14px}
.start-foot .mdi{color:#2F7D4F;font-size:18px}
.start-actions{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:12px;margin-top:30px;max-width:560px}
.start-actions .lg{margin-left:auto}
.start-actions.wide{max-width:none}
/* The live page's pair sits together at the right edge of the Copy buttons above it, the solid one last. */
.start-actions.live{max-width:none;justify-content:flex-end}
.start-actions.live .lg{margin-left:0}
.path-cards{display:flex;flex-direction:column;gap:14px}
.path-card{display:grid;grid-template-columns:auto minmax(0,1fr) auto auto;align-items:center;gap:18px;width:100%;padding:22px 22px;text-align:left;
  background:#fff;border:1px solid #E6DED2;border-radius:18px;box-shadow:var(--pb-shadow-card);cursor:pointer;font:inherit;color:#1F2328;
  transition:border-color .15s ease,box-shadow .15s ease,transform .15s ease}
.path-card:hover{border-color:rgba(180,71,43,.55);box-shadow:0 1px 2px rgba(60,42,24,.05),0 14px 34px rgba(60,42,24,.09);transform:translateY(-1px)}
.path-card.picked{border-color:#B4472B;box-shadow:0 0 0 3px rgba(180,71,43,.14)}
.path-icon{display:inline-grid;place-items:center;width:52px;height:52px;border-radius:14px;background:rgba(180,71,43,.12);color:#B4472B;font-size:26px}
.path-copy{display:flex;flex-direction:column;gap:4px}
.path-copy strong{font:600 19px/1.25 var(--font-display);letter-spacing:-.2px}
.path-copy span{color:#4F4A45;font-size:15px;line-height:1.45}
.path-time{font-size:12.5px;color:#6B635B;white-space:nowrap}
.path-arrow{font-size:22px;color:#B4472B}
.swatches{display:flex;flex-wrap:wrap;align-items:center;gap:10px}
.swatch{position:relative;display:inline-grid;place-items:center;width:40px;height:40px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1px #D4C9B8;
  cursor:pointer;color:#fff;font-size:20px;padding:0}
.swatch.on{box-shadow:0 0 0 2px #1F2328}
.swatch.custom{background:conic-gradient(#B4472B,#8A6A12,#2F6B4F,#1F6F78,#2B5F8A,#6B3E75,#B4472B);overflow:hidden;margin:0!important}
.swatch.custom input{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%}
.swatch.custom .mdi{text-shadow:0 1px 2px rgba(0,0,0,.4);pointer-events:none}
.swatch.custom:focus-within{outline:2px solid #B4472B;outline-offset:2px}
.hex{width:118px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;text-transform:uppercase}
.toolkit{margin:8px 0 6px;padding:20px 22px;border-radius:16px;background:#fff;border:1px solid #E6DED2;box-shadow:var(--pb-shadow-card)}
.toolkit-title{display:flex;align-items:center;gap:8px;margin:0 0 10px;font:600 18px/1.3 var(--font-display);color:#1F2328}
.toolkit-title .mdi{color:#B4472B}
.toolkit ul{margin:0 0 12px;padding-left:20px;color:#4F4A45;font-size:14.5px;line-height:1.7}
.toolkit p{margin:0;font-size:14px;color:#4F4A45}
.templates{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(250px,100%),1fr));gap:14px}
.template{display:flex;flex-direction:column;gap:8px;padding:18px;text-align:left;background:#fff;border:1px solid #E6DED2;border-radius:16px;cursor:pointer;
  font:inherit;color:#1F2328;transition:border-color .15s ease,box-shadow .15s ease}
.template:hover{border-color:rgba(180,71,43,.55)}
.template.on{border-color:#B4472B;box-shadow:0 0 0 3px rgba(180,71,43,.14)}
.template.locked{background:#FBF8F3;cursor:pointer}
.template.locked strong,.template.locked .template-q{color:#6B635B}
.template-top{display:flex;justify-content:space-between;align-items:center;gap:8px}
.template-top strong{font:600 16px/1.3 var(--font-body)}
.template-text{font-size:14px;color:#4F4A45}
.template-q{font:500 15px/1.45 var(--font-display);color:#1F2328}
.questions-pick{border:0;padding:0;margin:0 0 20px}
.questions-pick .pb-check{padding:10px 12px;border:1px solid #E6DED2;border-radius:12px;background:#fff;margin-bottom:8px!important}
.quotable{margin-left:8px;vertical-align:1px}
.question-edit{display:flex;gap:8px;align-items:center;margin-bottom:8px}
.done-mark{display:inline-grid;place-items:center;width:64px;height:64px;border-radius:50%;background:#E8F3EC;color:#2F7D4F;font-size:34px;margin-bottom:18px}
.share-grid{display:grid;grid-template-columns:auto minmax(0,1fr);gap:28px;margin-top:22px;max-width:760px}
.block-row{display:grid;grid-template-columns:minmax(0,1fr) 220px;gap:12px;align-items:start;padding:14px;margin-bottom:10px;background:#fff;border:1px solid #E6DED2;border-radius:14px}
.block-text{margin:0;font:500 15px/1.5 var(--font-display);color:#1F2328;white-space:pre-line;max-height:120px;overflow:auto}
.block-fields{display:flex;flex-direction:column;gap:8px}
.block-fields .pb-input{min-height:38px;padding:8px 10px;font-size:14px}
.attest{margin:4px 0 0}
.imported{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(260px,100%),1fr));gap:14px;max-height:460px;overflow:auto;padding:4px}
.pb-quote.small{padding:20px 18px 16px}
.pb-quote.small .pb-quote-text{font-size:15px;display:-webkit-box;-webkit-line-clamp:5;-webkit-box-orient:vertical;overflow:hidden}
.pb-quote.small .pb-quote-meta{margin-top:12px}
.layouts{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;max-width:640px}
.layout{display:flex;flex-direction:column;align-items:center;gap:10px;padding:16px 12px;background:#fff;border:1px solid #E6DED2;border-radius:16px;cursor:pointer;font:inherit;color:#1F2328}
.layout.on{border-color:#B4472B;box-shadow:0 0 0 3px rgba(180,71,43,.14)}
.layout.locked{background:#FBF8F3;color:#6B635B}
.layout-thumb{display:grid;width:100%;height:56px;gap:4px}
.layout-thumb i{display:block;border-radius:5px;background:rgba(180,71,43,.18)}
.layout-thumb.grid{grid-template-columns:1fr 1fr 1fr;grid-template-rows:1fr 1fr}
.layout-thumb.grid i:nth-child(1){grid-row:span 2}
.layout-thumb.cards{grid-template-columns:1fr 1fr 1fr}
.layout-thumb.carousel{grid-template-columns:.3fr 1fr .3fr}
.layout-thumb.carousel i:nth-child(2){background:rgba(180,71,43,.4)}
.layout-thumb.quote{grid-template-columns:1fr}
.layout-thumb.quote i:nth-child(n+2){display:none}
.start-preview{position:sticky;top:96px;align-self:start;display:flex;flex-direction:column;align-items:center}
.preview-label{display:flex;align-items:center;gap:6px;margin:0 0 14px;font:600 13px/1 var(--font-body);color:#6B635B;letter-spacing:.02em}
.phone-switch{display:none}
.preview-actions{display:none}
@media(max-width:1080px){
  .start-main.with-preview{grid-template-columns:minmax(0,1fr) 320px;gap:36px}
}
@media(max-width:900px){
  .start-main,.start-main.with-preview{grid-template-columns:minmax(0,1fr);padding:24px 16px 64px;gap:0}
  .start-title{font-size:32px;line-height:1.15;letter-spacing:-.6px}
  .start-lead{font-size:16px;margin-bottom:22px}
  .phone-hidden{display:none}
  .start-preview{position:static}
  .phone-switch{display:flex;flex-wrap:nowrap;justify-self:start;margin-bottom:22px}
  .phone-switch button{display:inline-flex;align-items:center;gap:6px;min-width:120px;min-height:44px;justify-content:center;white-space:nowrap}
  .preview-actions{display:flex;flex-wrap:wrap;justify-content:space-between;gap:10px;width:100%;max-width:340px;margin-top:20px}
  .preview-actions .lg{flex:1 1 auto}
  .block-fields .pb-input{font-size:16px}
  .start-progress-text,.start-email{display:none}
  .share-grid{grid-template-columns:minmax(0,1fr)}
  .layouts{grid-template-columns:repeat(2,minmax(0,1fr))}
}
@media(max-width:600px){
  .start-top{gap:12px;padding:12px 16px}
  .start-progress .dot{width:14px}
  .start-account{gap:10px}
  .start-progress+.start-account{margin-left:12px}
  .path-card{grid-template-columns:auto minmax(0,1fr);gap:14px;padding:18px}
  .path-time,.path-arrow{display:none}
  .path-icon{width:44px;height:44px;font-size:22px}
  .path-copy strong{font-size:17px}
  .start-actions .lg{width:100%;margin-left:0;order:-1}
  .start-actions .pb-button{width:100%}
  .block-row{grid-template-columns:minmax(0,1fr)}
}
/* Small phones (360 to 400 wide): a narrower progress bar, so "Log out" stays on one line. */
@media(max-width:400px){
  .start-top{gap:10px;padding:12px}
  .start-progress{gap:4px}
  .start-progress .dot{width:10px}
  .start-progress+.start-account{margin-left:8px}
}
@media(prefers-reduced-motion:reduce){.path-card{transition:none}.path-card:hover{transform:none}}
</style>
